import { qbJobName } from "@nmt/shared";
import { useEffect, useState } from "react";
import { errorMessage } from "../../api";
import { Alert, Button, Field, Loading, PageHeader, Panel, PanelHeader, TextAreaField } from "../../components/ui";
import { useSettings } from "./useSettings";

export function ListsSettings() {
  const { query, save } = useSettings();
  const [leadSources, setLeadSources] = useState<string[] | null>(null);
  const [contactRoles, setContactRoles] = useState<string[] | null>(null);
  const [fileCategories, setFileCategories] = useState<string[] | null>(null);
  const [photoTags, setPhotoTags] = useState<string[] | null>(null);
  const [appointmentTypes, setAppointmentTypes] = useState<string[] | null>(null);
  const [expenseCategories, setExpenseCategories] = useState<string[] | null>(null);
  const [permitCities, setPermitCities] = useState<string[] | null>(null);
  const [roofYears, setRoofYears] = useState("");
  const [otherYears, setOtherYears] = useState("");
  const [pattern, setPattern] = useState("");
  const [firstNumber, setFirstNumber] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [terms, setTerms] = useState("");
  useEffect(() => {
    const s = query.data?.settings;
    if (s && leadSources === null) {
      setLeadSources(s["lists.leadSources"]);
      setContactRoles(s["lists.contactRoles"]);
      setFileCategories(s["lists.fileCategories"]);
      setPhotoTags(s["lists.photoTags"]);
      setAppointmentTypes(s["lists.appointmentTypes"]);
      setExpenseCategories(s["lists.expenseCategories"]);
      setPermitCities(s["lists.permitCities"]);
      setRoofYears(String(s["warranty.roofYears"]));
      setOtherYears(String(s["warranty.otherYears"]));
      setTerms(s["warranty.terms"]);
      setPattern(s["jobs.qbNamePattern"]);
      setFirstNumber(String(s["jobs.firstNumber"]));
    }
  }, [query.data, leadSources]);
  if (query.isLoading || !leadSources || !contactRoles || !fileCategories || !photoTags || !appointmentTypes || !expenseCategories || !permitCities) return <Loading />;

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        setNotice(null);
        save.mutate(
          {
            "lists.leadSources": leadSources.map((s) => s.trim()).filter(Boolean),
            "lists.contactRoles": contactRoles.map((s) => s.trim()).filter(Boolean),
            "lists.fileCategories": fileCategories.map((s) => s.trim()).filter(Boolean),
            "lists.photoTags": photoTags.map((s) => s.trim()).filter(Boolean),
            "lists.appointmentTypes": appointmentTypes.map((s) => s.trim()).filter(Boolean),
            "lists.expenseCategories": expenseCategories.map((s) => s.trim()).filter(Boolean),
            "lists.permitCities": permitCities.map((s) => s.trim()).filter(Boolean),
            "warranty.roofYears": Number(roofYears),
            "warranty.otherYears": Number(otherYears),
            "warranty.terms": terms,
            "jobs.qbNamePattern": pattern,
            "jobs.firstNumber": Number(firstNumber),
          },
          { onSuccess: () => setNotice("Saved.") },
        );
      }}
    >
      <PageHeader title="Lists and job numbers" />
      {notice && <Alert tone="ok">{notice}</Alert>}
      {save.error && <Alert>{errorMessage(save.error)}</Alert>}
      <div className="grid gap-5 md:grid-cols-2">
        <ListEditor title="Lead sources" description="Where jobs come from. Used in reports." items={leadSources} onChange={setLeadSources} />
        <ListEditor title="Related contact roles" description="How a related contact is connected to a job or another contact (Spouse, Adjuster…). Contact types are under Contact types." items={contactRoles} onChange={setContactRoles} />
        <ListEditor title="File categories" description="How files on a job are sorted (Photos, Insurance, Permits…)." items={fileCategories} onChange={setFileCategories} />
        <ListEditor title="Photo tags" description="Tags to pick from on photos (Before, During, After, Roof…)." items={photoTags} onChange={setPhotoTags} />
        <ListEditor title="Appointment types" description="Kinds of appointments on the calendar (Inspection, Install…)." items={appointmentTypes} onChange={setAppointmentTypes} />
        <ListEditor title="Cities that need a permit" description="Jobs in these cities show a reminder until a permit is added." items={permitCities} onChange={setPermitCities} />
        <Panel>
          <PanelHeader title="Warranty" description="The workmanship warranty that starts when a job is closed." />
          <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
            <Field label="Residential roofing (years)" type="number" min={0} max={50} value={roofYears} onChange={(e) => setRoofYears(e.target.value)} />
            <Field label="Everything else (years)" type="number" min={0} max={50} value={otherYears} onChange={(e) => setOtherYears(e.target.value)} />
            <TextAreaField className="sm:col-span-2" label="What it covers (printed on the certificate)" rows={5} maxLength={1200} value={terms} onChange={(e) => setTerms(e.target.value)} />
          </div>
        </Panel>
        <ListEditor title="Expense categories" description="Categories for receipts and bills (Materials, Dump fees…). The receipt reader picks from these." items={expenseCategories} onChange={setExpenseCategories} />
      </div>
      <Panel>
        <PanelHeader title="Job numbers and QuickBooks names" description="JobNimbus numbers carry over on import; new jobs continue after the highest number." />
        <div className="grid gap-4 px-5 py-5 sm:grid-cols-2">
          <Field label="First job number (when there are no jobs yet)" inputMode="numeric" value={firstNumber} onChange={(e) => setFirstNumber(e.target.value.replace(/\D/g, ""))} />
          <Field
            label="QuickBooks name for new jobs"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            hint={`Use {number}, {lastName}, {street}, {type}. Example: ${qbJobName(pattern, { number: 1042, lastName: "Smith", type: "Insurance Claim", street: "100 Main St" })}. Changing this doesn't rename jobs already in QuickBooks; rename one on its page.`}
          />
        </div>
      </Panel>
      <div className="flex justify-end">
        <Button variant="primary" type="submit" busy={save.isPending}>
          Save
        </Button>
      </div>
    </form>
  );
}

function ListEditor({ title, description, items, onChange }: { title: string; description: string; items: string[]; onChange: (v: string[]) => void }) {
  const [adding, setAdding] = useState("");
  const move = (i: number, d: -1 | 1) => {
    const next = [...items];
    const j = i + d;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };
  return (
    <Panel>
      <PanelHeader title={title} description={description} />
      <ul className="px-5 py-3">
        {items.map((item, i) => (
          <li key={`${item}-${i}`} className="flex items-center gap-1 py-1">
            <input
              value={item}
              onChange={(e) => onChange(items.map((x, k) => (k === i ? e.target.value : x)))}
              className="flex-1 rounded-[var(--radius-control)] border border-line px-2.5 py-1.5 text-sm"
              aria-label={`${title} ${i + 1}`}
            />
            <button type="button" className="rounded px-1.5 text-muted hover:bg-mist" onClick={() => move(i, -1)} aria-label="Move up">
              ↑
            </button>
            <button type="button" className="rounded px-1.5 text-muted hover:bg-mist" onClick={() => move(i, 1)} aria-label="Move down">
              ↓
            </button>
            <button type="button" className="rounded px-1.5 text-muted hover:bg-danger-wash hover:text-danger" onClick={() => onChange(items.filter((_, k) => k !== i))} aria-label={`Remove ${item}`}>
              ×
            </button>
          </li>
        ))}
        <li className="mt-2 flex gap-2">
          <input
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (adding.trim()) {
                  onChange([...items, adding.trim()]);
                  setAdding("");
                }
              }
            }}
            placeholder="Add…"
            className="flex-1 rounded-[var(--radius-control)] border border-line px-2.5 py-1.5 text-sm"
            aria-label={`Add to ${title}`}
          />
          <Button
            type="button"
            className="px-3 py-1.5"
            onClick={() => {
              if (adding.trim()) {
                onChange([...items, adding.trim()]);
                setAdding("");
              }
            }}
          >
            Add
          </Button>
        </li>
      </ul>
    </Panel>
  );
}

